import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type { GroupListItem, GroupKind } from '../groupsSlice';

/**
 * Sharing-groups hub card. Sprint 1 transition: now consumes
 * `GroupListItem` (server-backed shape) instead of the legacy MockGroup.
 *
 * The richer permission badge + member-avatar stack from the mock-era
 * design is gone — the v1 API list response doesn't include per-member
 * detail or per-permission flags. CrewHubScreen rebuild (FE-8.2)
 * replaces this component entirely with a Crew-tile design that uses
 * `member_count` + `kind` chip instead.
 *
 * Until FE-8.2 lands, this card stays minimal but functional so the
 * legacy SharingScreen continues to compile + render.
 */
interface GroupCardProps {
  group: GroupListItem;
  onPress: () => void;
  onManagePress?: () => void;
  /** Visually "feature" the card (larger / solid purple) — used for the
      first group on the hub. */
  featured?: boolean;
}

const KIND_LABEL: Record<GroupKind, string> = {
  CREW: 'Circle',
  FAMILY: 'Family',
  TEAM: 'Team',
  FRIENDS: 'Friends',
  NEIGHBOURS: 'Neighbours',
  PERSONAL: 'Personal',
};

export const GroupCard: React.FC<GroupCardProps> = ({
  group,
  onPress,
  onManagePress,
  featured = false,
}) => {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.card,
        featured && styles.featured,
        pressed && styles.pressed,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`Open ${group.name}, ${group.member_count} member${group.member_count === 1 ? '' : 's'}`}
      // FE-258.3 — testID for E2E selectors. Specs find the card
      // by the group's numeric id.
      testID={`group-card-${group.id}`}
    >
      <View style={styles.headerRow}>
        <View
          style={[styles.iconTile, featured && styles.iconTileFeatured]}
        >
          <MaterialIcons
            name={group.kind === 'PERSONAL' ? 'person' : 'group'}
            size={20}
            color={featured ? fpColors.onPrimary : fpColors.primary}
          />
        </View>
        <View
          style={[styles.kindBadge, featured && styles.kindBadgeFeatured]}
        >
          <Text
            style={[styles.kindText, featured && styles.kindTextFeatured]}
          >
            {KIND_LABEL[group.kind]}
          </Text>
        </View>
      </View>

      <Text
        style={[styles.name, featured && styles.nameFeatured]}
        numberOfLines={1}
      >
        {group.name}
      </Text>
      {!!group.description && (
        <Text
          style={[styles.description, featured && styles.descriptionFeatured]}
          numberOfLines={2}
        >
          {group.description}
        </Text>
      )}

      <View style={styles.footer}>
        <Text style={[styles.metaText, featured && styles.metaTextFeatured]}>
          {group.member_count != null
            ? `${group.member_count} ${group.member_count === 1 ? 'member' : 'members'}`
            : 'Open to see members'}
          {group.item_count && group.item_count > 0
            ? ` · ${group.item_count} items`
            : ''}
        </Text>

        {onManagePress ? (
          <Pressable onPress={onManagePress} hitSlop={6}>
            <Text
              style={[styles.manageLink, featured && styles.manageLinkFeatured]}
            >
              Manage
            </Text>
          </Pressable>
        ) : (
          <MaterialIcons
            name="chevron-right"
            size={20}
            color={featured ? `${fpColors.onPrimary}cc` : fpColors.outline}
          />
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 14,
    padding: 16,
    gap: 10,
  },
  featured: {
    backgroundColor: fpColors.primaryContainer,
    borderColor: fpColors.primaryContainer,
  },
  pressed: { opacity: 0.9 },

  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconTile: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}14`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconTileFeatured: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  kindBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: `${fpColors.primary}14`,
  },
  kindBadgeFeatured: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  kindText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.4,
    color: fpColors.primary,
    textTransform: 'uppercase',
  },
  kindTextFeatured: {
    color: fpColors.onPrimaryContainer,
  },

  name: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
    letterSpacing: -0.3,
  },
  nameFeatured: {
    color: fpColors.onPrimaryContainer,
  },
  description: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
  },
  descriptionFeatured: {
    color: `${fpColors.onPrimaryContainer}cc`,
  },

  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
  },
  metaText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  metaTextFeatured: {
    color: `${fpColors.onPrimaryContainer}cc`,
  },
  manageLink: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
  manageLinkFeatured: {
    color: fpColors.onPrimaryContainer,
  },
});
